import "@/App.css";
import { lazy, Suspense } from "react";
import { MotionConfig } from "framer-motion";
import { BrowserRouter, matchPath, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { DASHBOARD_LEGACY_ROUTES, DASHBOARD_ROUTES, dashboardPathForTab } from "@/lib/dashboardRoutes";
import Landing from "@/pages/Landing";
import CosmicBackdrop from "@/components/CosmicBackdrop";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Verify = lazy(() => import("@/pages/Verify"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Changelog = lazy(() => import("@/pages/Changelog"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));

function PageFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-black/35 backdrop-blur-[2px]">
      <span className="text-sm tracking-[0.08em] text-[#6EC8FF] animate-pulse">Opening Raava…</span>
    </div>
  );
}

function Protected({ children, ownerOnly = false, requiredTool = "" }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen bg-black/35 flex items-center justify-center backdrop-blur-[2px]" data-testid="auth-loading">
        <span className="text-sm tracking-[0.08em] text-[#6EC8FF] animate-pulse">Checking your sign-in…</span>
      </div>
    );
  }
  if (!user) return <Navigate to="/?auth=login#access" replace state={{ returnTo: `${location.pathname}${location.search}${location.hash}` }} />;
  if (ownerOnly && !user.is_owner) return <Navigate to="/profile" replace />;
  if (requiredTool && !user.is_owner && !user.launcher_tools?.includes(requiredTool)) return <Navigate to="/my-permissions" replace />;
  return children;
}

function LegacyDashboardRedirect({ to }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <div className="min-h-screen bg-black/35 flex items-center justify-center backdrop-blur-[2px]"><span className="text-sm tracking-[0.08em] text-[#6EC8FF] animate-pulse">Checking your sign-in…</span></div>;
  }
  if (!user) return <Navigate to="/?auth=login#access" replace state={{ returnTo: `${location.pathname}${location.search}${location.hash}` }} />;
  const params = new URLSearchParams(location.search);
  const legacyTab = params.get("tab");
  params.delete("tab");
  const target = location.pathname === "/dashboard" && legacyTab
    ? dashboardPathForTab(legacyTab, Boolean(user.is_owner))
    : to;
  const search = params.toString();
  return <Navigate to={`${target}${search ? `?${search}` : ""}${location.hash}`} replace />;
}

function RouteBackdrop() {
  const location = useLocation();
  const dashboardRoute = DASHBOARD_ROUTES.some((route) => matchPath({ path: route.path, end: true }, location.pathname));
  return location.pathname === "/" || location.pathname === "/changelog" || dashboardRoute
    ? <CosmicBackdrop dashboardMode={dashboardRoute || location.pathname === "/changelog"} />
    : null;
}

export default function App() {
  return (
    <AuthProvider>
      <MotionConfig reducedMotion="user">
        <div className="App noise-overlay">
          <BrowserRouter>
            <RouteBackdrop />
            <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<Landing />} />
            <Route path="/verify" element={<Verify />} />
            <Route path="/forgot-password" element={<Navigate to="/?auth=forgot#access" replace />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/changelog"
              element={
                <Protected>
                  <Changelog />
                </Protected>
              }
            />
            <Route path="/terms" element={<LegalPage document="terms" />} />
            <Route path="/software-license" element={<LegalPage document="license" />} />
            <Route path="/privacy" element={<LegalPage document="privacy" />} />
            {DASHBOARD_LEGACY_ROUTES.map((route) => (
              <Route key={route.path} path={route.path} element={<LegacyDashboardRedirect to={route.to} />} />
            ))}
            {DASHBOARD_ROUTES.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <Protected ownerOnly={route.ownerOnly} requiredTool={route.requiredTool}>
                    <Dashboard initialTab={route.tab} />
                  </Protected>
                }
              />
            ))}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
          <Toaster theme="dark" position="bottom-right" />
        </div>
      </MotionConfig>
    </AuthProvider>
  );
}
